import { Controller } from "@hotwired/stimulus"
import { announcePopoverOpen, bindExclusivePopover, isPopoverOpen } from "../../utils/exclusive-popover.util"
import { installFilterHotkey } from "../../utils/filter-hotkey.util"
import { isCompactViewport } from "../../utils/pointer.util"
import { cancelPopoverClose, closeWithPopoverMotion } from "../../utils/popover-motion.util"

const ACTIVE_FILTER_QUERY_KEYS = [
  "orderNumber",
  "project",
  "createdBy",
  "approvalWorkflow",
  "expenseType",
  "expenseType[]",
  "paymentStatus",
  "createdFrom",
  "createdTo",
  "issuedFrom",
  "issuedTo",
  "amountFrom",
  "amountTo",
  "loan_id[]"
]

// Connects to data-controller="expenses--filter-menu"
// Handles UI state for the expenses filter menu
export default class extends Controller {
  static targets = [
    "activeFilterIndicator",
    "filterButton",
    "filterMenu",
    "filterItem",
    "filterContent",
    "searchClearButton",
    "searchInput",
    "activeStatusOrderNumber",
    "activeStatusProject",
    "activeStatusCreatedBy",
    "activeStatusApprovalWorkflow",
    "activeStatusExpenseType",
    "activeStatusPaymentStatus",
    "activeStatusCreatedAt",
    "activeStatusIssuedAt",
    "activeStatusAmount"
  ]

  connect() {
    this.#initializeActiveStatus()
    this.refreshActiveFilterIndicator()
    this.#restoreSearchFromUrl()
    this.boundHandleGlobalPointerDown = this.#handleGlobalPointerDown.bind(this)
    this.boundHandleResize = this.#handleResize.bind(this)
    this.boundRefreshActiveFilterIndicator = this.refreshActiveFilterIndicator.bind(this)
    this.unbindExclusivePopover = bindExclusivePopover(this.element, () => this.closeFilterMenu())
    this.removeFilterHotkey = installFilterHotkey(this)
    document.addEventListener("pointerdown", this.boundHandleGlobalPointerDown, true)
    window.addEventListener("resize", this.boundHandleResize, { passive: true })
    this.filterMenuTarget.addEventListener("change", this.boundRefreshActiveFilterIndicator)
    this.filterMenuTarget.addEventListener("input", this.boundRefreshActiveFilterIndicator)
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.boundHandleGlobalPointerDown, true)
    window.removeEventListener("resize", this.boundHandleResize)
    this.filterMenuTarget.removeEventListener("change", this.boundRefreshActiveFilterIndicator)
    this.filterMenuTarget.removeEventListener("input", this.boundRefreshActiveFilterIndicator)
    this.unbindExclusivePopover?.()
    this.removeFilterHotkey?.()
  }

  #initializeActiveStatus() {
    const urlSearchParams = new URLSearchParams(window.location.search)

    if (this.hasActiveStatusOrderNumberTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["orderNumber"]), this.activeStatusOrderNumberTarget)
    }
    if (this.hasActiveStatusProjectTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["project"]), this.activeStatusProjectTarget)
    }
    if (this.hasActiveStatusCreatedByTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["createdBy"]), this.activeStatusCreatedByTarget)
    }
    if (this.hasActiveStatusApprovalWorkflowTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["approvalWorkflow"]),
        this.activeStatusApprovalWorkflowTarget
      )
    }
    if (this.hasActiveStatusExpenseTypeTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["expenseType[]", "expenseType"]),
        this.activeStatusExpenseTypeTarget
      )
    }
    if (this.hasActiveStatusPaymentStatusTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["paymentStatus[]", "paymentStatus"]),
        this.activeStatusPaymentStatusTarget
      )
    }
    if (this.hasActiveStatusCreatedAtTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["createdFrom", "createdTo"]),
        this.activeStatusCreatedAtTarget
      )
    }
    if (this.hasActiveStatusIssuedAtTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["issuedFrom", "issuedTo"]),
        this.activeStatusIssuedAtTarget
      )
    }
    if (this.hasActiveStatusAmountTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["amountFrom", "amountTo"]),
        this.activeStatusAmountTarget
      )
    }
  }

  #toggleChecker(urlSearchParams, keys) {
    if (urlSearchParams.has("quickFilter")) return false

    return keys.some((key) => urlSearchParams.has(key))
  }

  toggleActiveStatus(isActive, target) {
    if (isActive) {
      target.classList.remove("d-none")
    } else {
      target.classList.add("d-none")
    }
  }

  refreshActiveFilterIndicator() {
    if (!this.hasActiveFilterIndicatorTarget) return

    const isActive = this.#hasActiveFilterQueryParams() || this.#hasActiveFilterControls()
    this.activeFilterIndicatorTarget.classList.toggle("d-none", !isActive)
    this.filterButtonTarget.classList.toggle("filter-button--has-active-filters", isActive)
  }

  openFilterMenu(event) {
    event?.preventDefault()
    if (this.#isPopoverOpen()) return
    announcePopoverOpen(this.element)
    this.#applyPopoverViewportSizing()
    cancelPopoverClose(this.filterMenuTarget)
    this.filterMenuTarget.showPopover()
    this.filterButtonTarget.classList.add("filter-button--active")
  }

  closeFilterMenu() {
    if (!this.#isPopoverOpen()) return
    closeWithPopoverMotion(this.filterMenuTarget, () => this.filterMenuTarget.hidePopover())
    this.filterButtonTarget.classList.remove("filter-button--active")
  }

  selectFilter(event) {
    const { currentTarget } = event
    const selectedId = currentTarget.dataset.id

    this.filterItemTargets.forEach((el) => {
      el.classList.toggle("sidebar__tab--active", el === currentTarget)
    })

    this.filterContentTargets.forEach((el) => {
      el.classList.toggle("tab-content--hidden", el.id !== selectedId)
    })
  }

  clearSearch(event) {
    event?.preventDefault()
    if (!this.hasSearchInputTarget) return

    this.searchInputTarget.value = ""
    this.searchInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    // The button that was just activated hides itself the moment the field
    // empties (the CSS keys it on `:placeholder-shown`), so focus would fall to
    // <body> and the next Tab would restart at the top of the document. The
    // field is still on screen at every width now, so focus always goes back to
    // it — there is no longer a collapsing row for focus to have nowhere to go.
    this.searchInputTarget.focus()
  }

  handleSearchKeydown(event) {
    if (event.key !== "Escape" || !this.#hasSearchValue()) return

    event.preventDefault()
    this.clearSearch(event)
  }

  #handleGlobalPointerDown(event) {
    if (!this.#isPopoverOpen()) return
    if (this.filterMenuTarget.contains(event.target)) return
    if (this.filterButtonTarget.contains(event.target)) return
    this.closeFilterMenu()
  }

  #handleResize() {
    if (this.#isPopoverOpen()) this.#applyPopoverViewportSizing()
  }

  #isPopoverOpen() {
    return isPopoverOpen(this.filterMenuTarget)
  }

  #applyPopoverViewportSizing() {
    if (!this.hasFilterMenuTarget || !this.hasFilterButtonTarget) return

    const buttonRect = this.filterButtonTarget.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const spacing = 16
    const isMobile = isCompactViewport()

    if (isMobile) {
      const mobileInset = 8
      const top = Math.max(mobileInset, Math.floor(buttonRect.bottom + mobileInset))
      const maxHeight = Math.max(320, viewportHeight - top - mobileInset)
      Object.assign(this.filterMenuTarget.style, {
        position: "fixed",
        top: `${top}px`,
        left: `${mobileInset}px`,
        right: `${mobileInset}px`,
        bottom: "auto",
        width: "auto",
        maxHeight: `${maxHeight}px`,
        zIndex: "1004"
      })
      this.filterMenuTarget.style.setProperty("--filter-menu-inset", `${mobileInset}px`)
      this.filterMenuTarget.style.setProperty("--filter-menu-max-height", `${maxHeight}px`)
      this.filterMenuTarget.dataset.mobileSheet = "true"
      return
    }

    for (const property of ["position", "top", "left", "right", "bottom", "width", "max-height", "z-index"]) {
      this.filterMenuTarget.style.removeProperty(property)
    }
    delete this.filterMenuTarget.dataset.mobileSheet
    this.filterMenuTarget.style.setProperty(
      "--filter-menu-max-height",
      `${Math.max(320, Math.floor(viewportHeight - buttonRect.bottom - spacing))}px`
    )
  }

  #hasActiveFilterQueryParams() {
    const urlSearchParams = new URLSearchParams(window.location.search)
    if (urlSearchParams.has("quickFilter")) return false

    return ACTIVE_FILTER_QUERY_KEYS.some((key) => urlSearchParams.getAll(key).some((value) => value.trim() !== ""))
  }

  #hasActiveFilterControls() {
    if (!this.hasFilterMenuTarget) return false

    return Array.from(this.filterMenuTarget.querySelectorAll("input, select, textarea")).some((element) => {
      if (!element.name) return false
      if (element.type === "checkbox" || element.type === "radio") return element.checked
      return element.value.trim() !== ""
    })
  }

  // The field is rendered with a nil value and marked data-turbo-permanent, so
  // on a full page load of a search URL the markup carries no value at all and
  // there is no old node for Turbo to carry one over from. Put it back from the
  // URL, which is where a shared or reloaded search link keeps it.
  #restoreSearchFromUrl() {
    if (!this.hasSearchInputTarget || this.searchInputTarget.value) return

    this.searchInputTarget.value = new URLSearchParams(window.location.search).get("search") || ""
  }

  #hasSearchValue() {
    return this.hasSearchInputTarget && this.searchInputTarget.value.trim() !== ""
  }
}
